import React, { useContext } from "react";
import { InferType, object, string } from "yup";
import i18n, { useTranslation } from "utilities/i18n";
import { nameIsUnassigned } from "utilities/stringUtils";
import { Form, Formik, Input } from "collections/Form";
import { Loader } from "elements";
import {
  FormInput,
  SettingsHeader
} from "routes/Manage/Sites/Settings/GeneralSiteSettings";
import { positiveNumberFilter } from "../Create/CreateRoomModal";
import { Layout } from "views/Main/Layouts";
import { Redirect } from "polyrouter";
import {
  useRoomQuery,
  useSwitchRoomSiteMutation,
  useUpdateRoomMutation
} from "generated/graphql";
import useSitesAndRooms from "routes/Manage/Register/useSitesAndRooms";
import { DISMISS, useNotification } from "collections/Notifications";
import {
  FormSelect,
  SettingsCategory
} from "routes/Manage/Inventory/Settings-Shared/DeviceSettingsComponents";
import { needs, useGuard } from "rbac";
import BlockingForm from "collections/Form/BlockingForm";
import { UNASSIGNED } from "Constants";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

export type RoomSize = "small" | "med" | "large" | "open";

export const RoomSettingsSchema = object({
  roomName: string()
    .label("NAME")
    .test(
      UNASSIGNED,
      i18n.t("ERROR_UNASSIGNED_ROOM_NAME"),
      (value: string) => !nameIsUnassigned(value)
    )
    .required()
    .ensure(),
  siteId: string()
    .label("SITE")
    .required(),
  floor: string().label("FLOOR_NAME"),
  capacity: string()
    .matches(/^\d+$/)
    .label("CAPACITY")
});

type GeneralSettings = InferType<typeof RoomSettingsSchema>;

export const GeneralRoomSettings: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation();
  const { Content } = Layout;
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";
  const userCanEdit = useGuard(needs("UPDATE_SITES"));

  const { data, fetching, executor } = useRoomQuery({
    variables: {
      id
    }
  });
  const refresh = () => executor({ requestPolicy: "network-only" });

  const { sites, siteMeta } = useSitesAndRooms();
  const [, updateRoomDetails] = useUpdateRoomMutation();
  const [, switchRoomSite] = useSwitchRoomSiteMutation();
  const [addNotification] = useNotification();

  if (nameIsUnassigned(data?.room?.name)) {
    return <Redirect to="/manage/sites" />;
  }

  const initialValues = {
    roomName: data?.room?.name || "",
    floor: data?.room?.floor || "",
    siteId: data?.room?.site.id || "",
    capacity: data?.room?.capacity != null ? data.room.capacity.toString() : ""
  };

  const handleSubmit = async (values: GeneralSettings) => {
    const site = siteMeta?.sites.find(site => site.id === values.siteId);

    if (!site) {
      return;
    } else if (site.id !== data?.room?.site.id) {
      // Switch site request
      const newSite = await switchRoomSite({
        tenantId: tenantId ?? "",
        roomId: id,
        siteId: site.id
      });

      if (newSite.error) {
        addNotification({
          type: "error",
          message: t("ERROR_UPDATING_ROOM"),
          dismiss: DISMISS.DEFAULT
        });
        return;
      }
    }

    const resp = await updateRoomDetails({
      details: {
        id,
        siteId: values.siteId,
        name: values.roomName,
        floor: values.floor,
        tenantId: tenantId,
        // Sets capacity to -1 if field is empty. Prospector service clears
        // capacity if it receives -1 and leaves unchanged if it receives null.
        capacity: !values.capacity ? -1 : parseInt(values.capacity)
      }
    });

    if (resp.error || resp.data?.upsertRoom === null) {
      addNotification({
        type: "error",
        message: t("ERROR_UPDATING_ROOM"),
        dismiss: DISMISS.DEFAULT
      });
    } else if (resp.data?.upsertRoom) {
      addNotification({
        type: "success",
        message: t("SUCCESSFULLY_UPDATED_ROOM"),
        dismiss: DISMISS.DEFAULT
      });
      refresh();
    }
  };

  const checkDuplicates = (values: GeneralSettings) => {
    let errors: any = {};
    if (values.siteId !== data?.room?.site.id) {
      const site = siteMeta?.sites.find(site => site.id === values.siteId);

      if (site && site.rooms.find(room => room.name === data?.room?.name)) {
        errors["siteId"] = t("ERROR_DUPLICATE_ROOM", {
          room: data?.room?.name
        });
      }
    }
    return errors;
  };

  return (
    <Content className="settings">
      <Loader loading={fetching}>
        <Formik
          name="RoomGeneralSettings"
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={RoomSettingsSchema}
          validate={checkDuplicates}
          enableReinitialize
        >
          {({ dirty, setFieldValue }) => {
            return (
              <BlockingForm unsavedChanges={dirty}>
                <Form className="form grid" data-testid="GeneralRoomSettings">
                  <SettingsHeader
                    title={initialValues.roomName}
                    userCanEdit={userCanEdit}
                  />
                  <SettingsCategory title={t("GENERAL")}>
                    <FormInput
                      label={t("ROOM_NAME")}
                      name="roomName"
                      tabIndex={0}
                      disabled={!userCanEdit}
                    />
                    <FormSelect
                      label={t("SITE")}
                      name="siteId"
                      tabIndex={1}
                      options={sites}
                      search
                      disabled={!userCanEdit}
                      permission={"UPDATE_TENANT"}
                    />
                    <FormInput
                      label={t("FLOOR_NAME")}
                      name="floor"
                      tabIndex={2}
                      disabled={!userCanEdit}
                    />
                    <div className="form-group">
                      <label
                        className="col-4 text-right"
                        htmlFor="capacity-input"
                      >
                        {t("CAPACITY_OPTIONAL")}:
                      </label>
                      <div className="col-2">
                        <Input
                          name="capacity"
                          type="number"
                          onKeyPress={positiveNumberFilter}
                          onChange={e => {
                            if (
                              !e.target.value ||
                              Number(e.target.value) >= 0
                            ) {
                              setFieldValue("capacity", e.target.value);
                            }
                          }}
                          tabIndex={3}
                          disabled={!userCanEdit}
                        />
                      </div>
                    </div>
                  </SettingsCategory>
                </Form>
              </BlockingForm>
            );
          }}
        </Formik>
      </Loader>
    </Content>
  );
};
